import type { ListingTypeOptions } from "@/utils/productTypes";
import { Models } from "api/types";

import { formatCurrency } from "../toolbox/Money";

export const hasLinearPricing = (
  packagePrices: Models<"PackagePriceModel">[] | undefined
) => {
  if (!packagePrices) return false;
  return packagePrices
    .map((item: any, index: any, array: any) => {
      if (index === 0) return true; // First element always follows a linear pattern
      return item["quantity"] === array[index - 1]["quantity"] + 1;
    })
    .every(Boolean);
};

export const getPricingText = (
  hasLinearPricing: boolean | undefined,
  price: number,
  unitType: string | null | undefined,
  type: ListingTypeOptions
) => {
  const fromText = !hasLinearPricing && type === "Treatment" ? "From " : "";
  const formattedPrice = formatCurrency(price);
  let listingUnitType = hasLinearPricing ? `/${unitType}` : "";

  if (type == "Membership") {
    listingUnitType = "/month";
  }
  return fromText + formattedPrice + listingUnitType;
};

export const getDiscountText = (
  shopListingDiscounts: Models<"ShopProductDiscountModel"> | undefined
) => {
  const { maxAmountOff, maxPercentageOff, isVariable } =
    shopListingDiscounts || {};

  const isBuyXGetY = shopListingDiscounts?.isBuyXGetY ?? false;

  const amountOff = maxAmountOff && maxAmountOff > 0 ? maxAmountOff : undefined;
  const percentageOff =
    maxPercentageOff && maxPercentageOff > 0 ? maxPercentageOff : undefined;

  if (!amountOff && !percentageOff) return "";

  if (isBuyXGetY) {
    return getTagBXGYDiscountText(
      shopListingDiscounts?.matchesRequirementsAndInclusions ?? false,
      percentageOff
    );
  }

  const amountText = amountOff ? `${formatCurrency(amountOff)} off` : "";
  const percentageText = percentageOff
    ? `${Math.round(percentageOff * 100)}% off`
    : "";
  const prefix = isVariable && (amountText || percentageText) ? "Up to " : "";

  return `${prefix}${amountText || percentageText}`;
};

export const getTagBXGYDiscountText = (
  isSamePackage: boolean,
  percentOff: number | undefined
) => {
  if (isSamePackage && percentOff !== 1) {
    return "Buy 1, Get 1";
  }

  if (percentOff === 1 || (isSamePackage && percentOff === 1)) {
    return "Free Gift";
  }

  return "Buy more & save";
};

export const getDiscountCustomColor = (
  shopListingDiscounts: Models<"ShopProductDiscountModel"> | undefined,
  customShopCategories: Models<"CategoryListingsResult">[] | undefined
) => {
  if (!shopListingDiscounts) return null;
  return shopListingDiscounts?.isFromSiteWidePromo &&
    customShopCategories &&
    customShopCategories.length > 0
    ? customShopCategories[0].promotion?.backgroundColor
    : null;
};

export const getListingsWithDiscountInformation = <T extends { id: string }>(
  listings: T[],
  customShopCategories:
    | Models<"CategoryListingsResult">[]
    | undefined = undefined,
  listingDiscounts: {
    [key: string]: Models<"ShopProductDiscountModel"> | undefined;
  } = {}
) => {
  if (!listings?.length) return [];
  return listings.map((listing) => {
    const discountData = listingDiscounts[listing.id];

    return {
      ...listing,
      discountText: getDiscountText(discountData),
      discount: discountData,
      customColor: getDiscountCustomColor(discountData, customShopCategories),
    };
  });
};

export const getMembershipsWithDiscountInformation = (
  memberships: Models<"MembershipModel">[],
  shopDiscounts: Models<"ShopDiscountModel"> | undefined,
  customShopCategories: Models<"CategoryListingsResult">[] | undefined
) => {
  if (!memberships || !memberships.length) return [];
  return memberships.map((membership) => {
    return {
      ...membership,
      discountText: getDiscountText(
        shopDiscounts?.membershipDiscounts[membership.id]
      ),
      customColor: getDiscountCustomColor(
        shopDiscounts?.membershipDiscounts[membership.id],
        customShopCategories
      ),
      discount: shopDiscounts?.membershipDiscounts[membership.id],
    };
  });
};

export const getRetailProductsWithDiscountInformation = <
  T extends { id: string }
>(
  retailProducts: T[],
  customShopCategories:
    | Models<"CategoryListingsResult">[]
    | undefined = undefined,
  listingDiscounts: {
    [key: string]: Models<"ShopProductDiscountModel"> | undefined;
  } = {}
) => {
  if (!retailProducts?.length) return [];
  return retailProducts.map((product) => {
    const discountData = listingDiscounts[product.id];

    return {
      ...product,
      discountText: getDiscountText(discountData),
      discount: discountData,
      customColor: getDiscountCustomColor(discountData, customShopCategories),
    };
  });
};

export const getInclusionsCount = (
  inclusions: Models<"InclusionListingsModel"> | undefined
): number => {
  if (!inclusions) return 0;

  const packageCount = inclusions.packages.length;
  const treatmentCount = inclusions.treatments.reduce(
    (acc, t) => acc + (t.variants.length || 1),
    0
  );
  const membershipCount = inclusions.memberships.length;
  const productCount = inclusions.products.length;

  return packageCount + treatmentCount + membershipCount + productCount;
};
